import { loadScript } from "app/client/lib/loadScript";
import { reportError } from "app/client/models/errors";
import { LoginPageConfig } from "app/common/LoginPageConfig";
import { getGristConfig } from "app/common/urlUtils";

import { Disposable, DomContents, styled } from "grainjs";

/**
 * Minimal typings for the Turnstile JS API.
 *
 * See https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/.
 */
interface TurnstileAPI {
  render(el: HTMLElement, params: { sitekey: string }): string;
  reset(widgetId?: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/**
 * A Cloudflare Turnstile CAPTCHA widget, for embedding in a form. When a challenge is completed,
 * the widget adds a hidden "cf-turnstile-response" input to the enclosing form, with a token that
 * the server verifies (see `Turnstile.ts`).
 *
 * If Turnstile (i.e.`turnstileSiteKey`) is not configured, builds nothing.
 */
export class TurnstileWidget extends Disposable {
  private _widgetId?: string;

  constructor() {
    super();
    this.onDispose(() => this._widgetId ? window.turnstile?.remove(this._widgetId) : null);
  }

  public buildDom(): DomContents {
    const { turnstileSiteKey } = getGristConfig() as LoginPageConfig;
    if (!turnstileSiteKey) { return null; }

    return cssTurnstileContainer((elem) => {
      loadTurnstile()
        .then((turnstile) => {
          if (this.isDisposed()) { return; }
          this._widgetId = turnstile.render(elem, { sitekey: turnstileSiteKey });
        })
        .catch(reportError);
    });
  }

  /**
   * Requests a fresh token. Call after a failed form submission, as tokens are single-use.
   */
  public reset() {
    if (this._widgetId) {
      window.turnstile?.reset(this._widgetId);
    }
  }
}

async function loadTurnstile(): Promise<TurnstileAPI> {
  if (!window.turnstile) { await loadScript(TURNSTILE_SCRIPT_URL); }
  return window.turnstile!;
}

const cssTurnstileContainer = styled("div", `
  margin-bottom: 24px;
`);
